import { sessionStore, sessionLoad } from '../services/storageService';
import util from "../services/utilService";
import { SelectedFilter } from '../models/Filter';
import filterService, { resolveServikSelectionToIds } from '../services/filterService';
import { updateCurrentFilterState } from '../../performance/services/performanceService';

/**
 * @typedef {import('../models/Filter').FilterOptions} FilterOptions 
 * @typedef {import('../models/Filter').FilterSelected} FilterSelected 
 * @typedef {'company' | 'site' | 'timeType' | 'building' | 'floor' | 'gender' | 'shifts' | 'days' | 'rooms' | 'cleaners' | 'time' | 'viewTimeBy' | 'servikName' | 'hour'} FilterSelectedFields
 * @typedef {{
 * filterOptions: FilterOptions | null;
 * filterSelected: FilterSelected | null;
 * isAdditionalFilterOpen: boolean;
 * isToggleFilterDisabled: boolean;
 * }} State
*/

export default {
    /**@type {State} */
    state: {
        filterOptions: null,
        filterSelected: null,
        isAdditionalFilterOpen: false,
        isToggleFilterDisabled: false,
        isActiveFilter: false,
        isRestoringFilterState: false, // Flag to prevent watchers from firing during restoration
        filterJustApplied: false, // Flag to trigger updates when filter is applied (even if values didn't change)
        additionalActiveFilter: {
            rooms: true,
            building: true,
            floor: true,
            gender: true,
            shift: true,
            days: true,
            timeType:true,
            servikName: true,
            cleaners: true
        },
        currentSiteInfo: null
    },
    getters: {
        /**@param {State} state */
        currentFilteringSite(state) {
            const company = state.filterOptions.companies.find(company => {
                return state.filterSelected.company === company.id;
            })
            const { sites } = company;
            return sites.find(site => site.id === state.filterSelected.site);
        },
        /**@param {State} state */
        roomIdMap(state) {
            const rooms = (state.filterOptions && state.filterOptions.rooms) || [];
            if (!Array.isArray(rooms)) return {};
            return rooms.reduce((roomMap, room) => {
                roomMap[room.id] = room;
                return roomMap;
            }, {});
        },
        filterSelected(state) {
            return state.filterSelected;
        },
        additionalActiveFilter(state) {
            return state.additionalActiveFilter;
        },
        isActiveFilter(state) {
            return state.isActiveFilter;
        },
        filterOptions(state) {
            if (!state.filterOptions) return null;
            /**@type {FilterOptions} */
            let filterOptionsCopy = JSON.parse(JSON.stringify(state.filterOptions));
            
            filterOptionsCopy.rooms = Array.isArray(filterOptionsCopy.rooms)
                ? filterOptionsCopy.rooms.filter(room => room.is_calculated === 1)
                : []
            filterOptionsCopy = {
                building: filterOptionsCopy.building,
                floor: filterOptionsCopy.floor,
                gender: filterOptionsCopy.gender,
                rooms: filterOptionsCopy.rooms,
                ...filterOptionsCopy
            }
            if (state.filterSelected) {
                /**@type {FilterSelected}*/
                const { timeType } = state.filterSelected;
                // get relevant viewBy options for last quarter & last year
                if (timeType === 'last_quarter' || timeType === 'last_year') {
                    filterOptionsCopy.viewBy = filterOptionsCopy.viewBy.filter(type => type.value !== 'day');
                    // Sort rooms by name for filter selector (same as below)
                    if (Array.isArray(filterOptionsCopy.rooms) && filterOptionsCopy.rooms.length > 0) {
                        filterOptionsCopy.rooms = [...filterOptionsCopy.rooms].sort((a, b) => {
                            const nameA = (a.name || '').toString().toLowerCase();
                            const nameB = (b.name || '').toString().toLowerCase();
                            return nameA.localeCompare(nameB);
                        });
                    }
                    return filterOptionsCopy;
                }
                const { building: currBuilding, floor: currFloors } = state.filterSelected
                if (currBuilding && currBuilding.length > 0) {
                    // Get relevant floors for the selected buildings
                    filterOptionsCopy.floor = currBuilding.flatMap(building =>
                        Object.keys(filterOptionsCopy.building[building] || {})
                    )
                    if (currFloors) {
                        // Filter rooms based on selected floors and buildings
                        filterOptionsCopy.rooms = filterOptionsCopy.rooms.filter(room =>
                            currBuilding.includes(room.building) && currFloors.includes(room.floor)
                        );
                    } else {
                        // Filter rooms based only on the selected buildings
                        filterOptionsCopy.rooms = filterOptionsCopy.rooms.filter(room =>
                            currBuilding.includes(room.building)
                        )
                    }
                }
            }
            // Clean all option arrays to remove null/empty values
            const cleanOptions = (arr) => {
                if (!Array.isArray(arr)) return arr;
                return arr.filter(item => {
                    if (item === null || item === undefined || item === '') return false;
                    if (typeof item === 'object') {
                        const hasValidId = item.id !== null && item.id !== undefined && item.id !== '';
                        const name = item.name;
                        const hasValidName = name !== null && name !== undefined && name !== '' && String(name).trim() !== '';
                        return hasValidId && hasValidName;
                    }
                    if (typeof item === 'string' && item.trim() === '') return false;
                    return true;
                });
            };
            
            // Clean building array (convert from object keys and filter)
            if (filterOptionsCopy.building && typeof filterOptionsCopy.building === 'object' && !Array.isArray(filterOptionsCopy.building)) {
                filterOptionsCopy.building = Object.keys(filterOptionsCopy.building).filter(b => b && String(b).trim() !== '');
            } else if (Array.isArray(filterOptionsCopy.building)) {
                filterOptionsCopy.building = cleanOptions(filterOptionsCopy.building);
            }
            
            // Clean all filter option arrays
            if (Array.isArray(filterOptionsCopy.rooms)) {
                filterOptionsCopy.rooms = cleanOptions(filterOptionsCopy.rooms);
                // Sort rooms by name (case-insensitive) for the filter selector
                filterOptionsCopy.rooms.sort((a, b) => {
                    const nameA = (a.name || '').toString().toLowerCase();
                    const nameB = (b.name || '').toString().toLowerCase();
                    return nameA.localeCompare(nameB);
                });
            }
            if (Array.isArray(filterOptionsCopy.gender)) {
                filterOptionsCopy.gender = cleanOptions(filterOptionsCopy.gender);
            }
            if (Array.isArray(filterOptionsCopy.shift)) {
                filterOptionsCopy.shift = cleanOptions(filterOptionsCopy.shift);
            }
            if (Array.isArray(filterOptionsCopy.days)) {
                filterOptionsCopy.days = cleanOptions(filterOptionsCopy.days);
            }
            if (Array.isArray(filterOptionsCopy.servikName)) {
                filterOptionsCopy.servikName = cleanOptions(filterOptionsCopy.servikName);
            }
            if (Array.isArray(filterOptionsCopy.floor)) {
                filterOptionsCopy.floor = cleanOptions(filterOptionsCopy.floor);
            }
            if (Array.isArray(filterOptionsCopy.cleaners)) {
                filterOptionsCopy.cleaners = cleanOptions(filterOptionsCopy.cleaners);
                filterOptionsCopy.cleaners.sort((a, b) => {
                    const nameA = (a.name || '').toString().toLowerCase();
                    const nameB = (b.name || '').toString().toLowerCase();
                    return nameA.localeCompare(nameB);
                });
            }
            
            return filterOptionsCopy;
        },
        isAdditionalFilterOpen(state) {
            return state.isAdditionalFilterOpen;
        },
        isToggleFilterDisabled(state) {
            return state.isToggleFilterDisabled;
        },
        isRestoringFilterState(state) {
            return state.isRestoringFilterState;
        },
        filterJustApplied(state) {
            return state.filterJustApplied;
        },
        currentSiteInfo(state) {
            return state.currentSiteInfo;
        }
    },
    mutations: {
        setFilterSelected(state, { filterSelected }) {
            const oldFilter = state.filterSelected ? JSON.parse(JSON.stringify(state.filterSelected)) : null;
            state.filterSelected = filterSelected;
            sessionStore('filterSelected', filterSelected);
            
            // Update current filter state in performance service to prevent stale API calls
            updateCurrentFilterState(filterSelected);
        },
        setFilterOptions(state, { filterOptions }) {
            state.filterOptions = filterOptions;
        },
        /**
         * @param {State} state
         * @param {{ 
         * field: FilterSelectedFields;
         * value: any;
         * }} param2
        */
        updateFilter(state, { field, value }) {
            if (!state.filterSelected) return;
            
            // Capture old value before any changes
            const oldValue = state.filterSelected[field] !== undefined 
                ? JSON.parse(JSON.stringify(state.filterSelected[field])) 
                : null;
            const oldFilterState = JSON.parse(JSON.stringify(state.filterSelected));
            const cascadingChanges = {};
            
            switch (field) {
                case 'timeType':
                    if (value !== 'custom_time') {
                        const oldTime = state.filterSelected.time;
                        state.filterSelected.time = util.getTimeByType(value);
                        if (oldTime !== state.filterSelected.time) {
                            cascadingChanges.time = { from: oldTime, to: state.filterSelected.time };
                        }
                        
                        const oldViewTimeBy = state.filterSelected.viewTimeBy;
                        if (value === 'last_quarter' || value === 'last_year') state.filterSelected.viewTimeBy = 'week';
                        else state.filterSelected.viewTimeBy = 'day';
                        if (oldViewTimeBy !== state.filterSelected.viewTimeBy) {
                            cascadingChanges.viewTimeBy = { from: oldViewTimeBy, to: state.filterSelected.viewTimeBy };
                        }
                    }
                    if (!value) {
                        value = 'last_month'
                    }
                    break;
                case 'building':
                    if (state.filterSelected.rooms !== null) {
                        cascadingChanges.rooms = { from: state.filterSelected.rooms, to: null };
                    }
                    if (state.filterSelected.floor !== null) {
                        cascadingChanges.floor = { from: state.filterSelected.floor, to: null };
                    }
                    state.filterSelected.rooms = null;
                    state.filterSelected.floor = null;
                    break;
                case 'floor':
                    if (state.filterSelected.rooms !== null) {
                        cascadingChanges.rooms = { from: state.filterSelected.rooms, to: null };
                    }
                    state.filterSelected.rooms = null;
                    break;
                case 'company': {
                    const firstSiteId = state.filterOptions.companies.find(c => c.id === value).sites[0].id;
                    const oldCompany = state.filterSelected.company;
                    const oldSite = state.filterSelected.site;
                    const oldServikName = state.filterSelected.servikName;
                    state.filterSelected = new SelectedFilter(value, firstSiteId);
                    state.filterSelected.servikName = null;
                    updateCurrentFilterState(state.filterSelected);
                    if (oldCompany !== value) {
                        cascadingChanges.company = { from: oldCompany, to: value };
                    }
                    if (oldSite !== firstSiteId) {
                        cascadingChanges.site = { from: oldSite, to: firstSiteId };
                    }
                    if (oldServikName !== null) {
                        cascadingChanges.servikName = { from: oldServikName, to: null };
                    }
                    break;
                }
                case 'site':
                    const oldSite = state.filterSelected.site;
                    const oldServikName = state.filterSelected.servikName;
                    state.filterSelected = new SelectedFilter(state.filterSelected.company, value);
                    state.filterSelected.servikName = null;
                    updateCurrentFilterState(state.filterSelected);
                    if (oldSite !== value) {
                        cascadingChanges.site = { from: oldSite, to: value };
                    }
                    if (oldServikName !== null) {
                        cascadingChanges.servikName = { from: oldServikName, to: null };
                    }
                    break;
                case 'servikName': {
                    if (value == null || value === '') {
                        value = null
                    } else {
                        const list = Array.isArray(value) ? value : [value]
                        const ids = list
                            .map((item) => Number(item))
                            .filter((id) => Number.isFinite(id))
                        value = ids.length ? ids : null
                    }
                    break
                }
                case 'hour':
                    if (state.filterSelected[field] === value) value = null;
                    break;
                case 'rooms': {
                    // When rooms are selected, clear building, floor, and gender
                    // (floor depends on building, so it should be cleared too)
                    if (value && (Array.isArray(value) ? value.length > 0 : value !== null)) {
                        if (state.filterSelected.building !== null) {
                            cascadingChanges.building = { from: state.filterSelected.building, to: null };
                        }
                        if (state.filterSelected.floor !== null) {
                            cascadingChanges.floor = { from: state.filterSelected.floor, to: null };
                        }
                        if (state.filterSelected.gender !== null) {
                            cascadingChanges.gender = { from: state.filterSelected.gender, to: null };
                        }
                        state.filterSelected.building = null;
                        state.filterSelected.floor = null;
                        state.filterSelected.gender = null;
                    }
                    state.filterSelected.rooms = value;
                    break;
                }
                case 'time':
                    state.filterSelected.time = value;
                    break;
            }
            
            // Only update if value actually changed (for cases where we set it in the switch)
            if (state.filterSelected[field] !== value) {
                state.filterSelected[field] = value;
            }
            
            // Only save to session storage if filter modal is NOT open AND we're not restoring state
            // This prevents saving unapplied filter selections (like shift/days)
            // The filter will be saved when user clicks "Apply" in activateFilter
            if (!state.isAdditionalFilterOpen && !state.isRestoringFilterState) {
                sessionStore('filterSelected', state.filterSelected);
            }
            
            // Ensure current filter state is updated (company/site already updated above, but ensure others are synced)
            if (field !== 'company' && field !== 'site') {
                updateCurrentFilterState(state.filterSelected);
            }
        },
        setToggleFilterDisabled(state, { value }) {
            state.isToggleFilterDisabled = value;
        },
        setFilterStatus(state, { value }) {
            state.isActiveFilter = value;
        },
        toggleAdditionalFilter(state, { value }) {
            if (value || value === false) state.isAdditionalFilterOpen = value;
            else state.isAdditionalFilterOpen = !state.isAdditionalFilterOpen;
        },
        setRestoringFilterState(state, { value }) {
            state.isRestoringFilterState = value;
        },
        setFilterJustApplied(state, { value }) {
            state.filterJustApplied = value;
        },
        setCurrentSiteInfo(state, { currentSiteInfo }) {
            state.currentSiteInfo = currentSiteInfo;
        }
    },
    actions: {
        async loadFilterOptionsAndSelection({ commit, state }) {
            let user = null;
            try { user = sessionLoad('loggedUser'); } catch { user = null; }
            if (!user) return;

            let savedFilter = null;
            try { savedFilter = sessionLoad('filterSelected'); } catch { savedFilter = null; }

            const companiesWithSites = await filterService.getSiteCompanyList();

            let firstCompanyId = null, firstSiteId = null;
            if (companiesWithSites.length) {
                const firstCompany = companiesWithSites[0];
                firstCompanyId = firstCompany.id;
                firstSiteId = firstCompany.sites?.[0]?.id || null;
            }

            /**@type {SelectedFilter} */
            const filterSelected = savedFilter || new SelectedFilter(firstCompanyId, firstSiteId);

            if (filterSelected.servikName != null && !Array.isArray(filterSelected.servikName)) {
                filterSelected.servikName = filterSelected.servikName ? [filterSelected.servikName] : null
            } else if (Array.isArray(filterSelected.servikName) && filterSelected.servikName.length === 0) {
                filterSelected.servikName = null
            }

            if (filterSelected.site == null || filterSelected.site === '') {
                commit({ type: 'setFilterSelected', filterSelected });
                commit({
                    type: 'setFilterOptions',
                    filterOptions: {
                        companies: companiesWithSites,
                        timeType: ['custom_time', 'last_day', 'last_week', 'last_month', 'last_quarter'],
                        viewBy: [],
                        rooms: [], building: [], floor: [], gender: [], shift: [], days: [], servikName: [], cleaners: []
                    }
                });
                return filterSelected;
            }

            const existingSiteInfo = state.currentSiteInfo;
            const shouldFetchSite = !existingSiteInfo || String(filterSelected.site) !== String(existingSiteInfo.id);

            const [filterOptions, currentSiteInfo] = await Promise.all([
                filterService.getFilterOptions(filterSelected.site, companiesWithSites),
                shouldFetchSite
                    ? filterService.getSiteDetails(filterSelected.site)
                    : Promise.resolve(existingSiteInfo)
            ]);

            if (filterSelected.servikName != null) {
                const resolvedIds = resolveServikSelectionToIds(
                    filterSelected.servikName,
                    filterOptions.servikName || [],
                )
                filterSelected.servikName = resolvedIds.length ? resolvedIds : null
            }

            commit({ type: 'setFilterOptions', filterOptions });
            commit({ type: 'setFilterSelected', filterSelected });

            if (currentSiteInfo) {
                commit({ type: 'setCurrentSiteInfo', currentSiteInfo });
            }
            return filterSelected;
        },
        async loadUpdatedOptions({ commit, state }) {
            const companiesWithSites = await filterService.getSiteCompanyList()
            const filterOptions = await filterService.getFilterOptions(state.filterSelected.site, companiesWithSites)
            commit({ type: 'setFilterOptions', filterOptions })
            if (state.filterSelected?.servikName != null) {
                const resolvedIds = resolveServikSelectionToIds(
                    state.filterSelected.servikName,
                    filterOptions.servikName || [],
                )
                commit({
                    type: 'updateFilter',
                    field: 'servikName',
                    value: resolvedIds.length ? resolvedIds : null,
                })
            }
            return filterOptions;
        },
        async loadCurrentSiteInfo({ commit, state }) {
            if (!state.filterSelected) return;
            const currentSiteInfo = await filterService.getSiteDetails(state.filterSelected.site);
            commit({ type: 'setCurrentSiteInfo', currentSiteInfo });
        }
    },
}